import { dotRefreshKeyIssues } from './contract.js';
import type { BalanceProfile } from '../profile.js';
import { analyzeSafety, type Violation } from '../safety.js';
import { compileCanonicalSkill } from './compiler.js';
import { assessInContext, castLimitBreach, type OrbitMeasurementGrant } from './composite-capability.js';
import { compileCompositeSkill, type CompiledSkillPlanV3 } from './composite-compiler.js';
import { assessCompositeSafety } from './composite-safety.js';
import { CANONICAL_SKILL_V3_SCHEMA_VERSION, type CanonicalSkillV3Input } from './composite-types.js';
import { lowerDegenerateV3ToV2 } from './composite-upgrade.js';
import { validateCompositeSkill } from './composite-validate.js';
import { importLegacySkill, type LegacySkillV1 } from './legacy-import.js';
import { legalityContextKey, persistedSkillContext, type LegalityContext } from './legality-context.js';
import { loadOnce } from './load-cache.js';
import { CANONICAL_SKILL_V2_SCHEMA_VERSION, type CanonicalSkillV2, type CanonicalSkillV2Input } from './types.js';

export type SkillLoadResult =
  | { readonly status: 'READY'; readonly skill: CanonicalSkillV2; readonly migrated: boolean }
  /**
   * A verified composite skill, loaded and compiled.
   *
   * Deliberately a DIFFERENT status from `READY` rather than a V2 in
   * disguise: a composite cast has no faithful single-application form, and
   * every existing caller branches on `status === 'READY'`, so they all keep
   * refusing what they cannot execute instead of silently receiving one
   * stage of several. Composite-aware callers opt in by handling this case.
   */
  | { readonly status: 'READY_COMPOSITE'; readonly plan: CompiledSkillPlanV3; readonly migrated: boolean }
  | { readonly status: 'INVALID'; readonly issues: readonly { readonly path: string; readonly code: string; readonly message: string }[] }
  /**
   * A structurally valid V3 skill whose shape the current executable set cannot
   * resolve (or evidence the engine does not have yet). Returned with the authored input intact so nothing the player
   * asked for is lost. It is not an error and not an impossibility claim.
   */
  | { readonly status: 'DEFERRED'; readonly code: string; readonly input: unknown };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export interface ExecutableLegalityOptions {
  /** Binds a composite plan's randomised placements (a `SCATTER`'s centres); the default is for callers that only ask whether a skill loads. */
  readonly castSeed?: number;
  readonly grant?: OrbitMeasurementGrant;
}

/**
 * Whether a definition is a well-formed skill the engine can execute in `context`, before the profile's safety limits
 * are applied: its shape (the validators and the compile) is INVALID; a capability the engine has not opened, or
 * evidence it lacks, is DEFERRED; a cast-wide limit the cast exceeds (event depth, attack workload, the cast's attack
 * budget) is INVALID. READY and READY_COMPOSITE are told apart by what the definition compiles to.
 *
 * This is what authoring and inspection callers ask: a candidate that is not yet within the profile's limits is still
 * a loadable candidate. Only the loader's own context is remembered per definition.
 */
export function loadInContext(input: unknown, context: LegalityContext, options: ExecutableLegalityOptions = {}): SkillLoadResult {
  const castSeed = options.castSeed ?? 0;
  const assess = (definition: unknown): SkillLoadResult => assessUncached(definition, context, castSeed, options.grant);
  return isPersistedContext(context) ? loadOnce(input, castSeed, options.grant, assess) : assess(input);
}

/**
 * The one verdict on whether a definition may be owned and cast in `context`: `loadInContext` first, then the context
 * profile's safety limits on every stage it compiles to (INVALID). The first failure wins, so a definition that needs
 * a closed capability is DEFERRED however unsafe it also is.
 */
export function assessExecutableLegality(input: unknown, context: LegalityContext, options: ExecutableLegalityOptions = {}): SkillLoadResult {
  const loaded = loadInContext(input, context, options);
  if (loaded.status === 'READY_COMPOSITE') {
    const legality = assessPlanLegality(loaded.plan, context, options.grant);
    return legality.status === 'INVALID' ? { status: 'INVALID', issues: legality.violations.filter((violation) => violation.severity === 'error').map(issueOf) } : loaded;
  }
  if (loaded.status !== 'READY') return loaded;
  const compiled = compileCanonicalSkill(loaded.skill);
  if (compiled.status !== 'COMPILED') return loaded;
  const safety = analyzeSafety(compiled.plan.runtimeSkill, withHostLimits(context));
  return safety.ok ? loaded : { status: 'INVALID', issues: safety.violations.filter((violation) => violation.severity === 'error').map(issueOf) };
}

/** The context profile with the host's statically decidable limits (secondary attacks spawned, proc generation) applied where they are tighter. */
function withHostLimits(context: LegalityContext): BalanceProfile {
  const { profile, hostLimits } = context;
  const { maxSpawnedAttacks, maxProcGeneration } = profile.limits;
  return {
    ...profile,
    limits: { ...profile.limits, maxSpawnedAttacks: Math.min(maxSpawnedAttacks, hostLimits.maxSpawnedAttacks), maxProcGeneration: Math.min(maxProcGeneration, hostLimits.maxProcGeneration) },
  };
}

const issueOf = (violation: Violation) => ({ path: violation.path ?? '', code: violation.code, message: violation.message });

/** The verdict on a compiled composite plan: supported, deferred, or invalid with every violation the refusing check reported (errors and warnings). */
export type PlanLegality =
  | { readonly status: 'SUPPORTED' }
  | { readonly status: 'DEFERRED'; readonly code: string; readonly message: string }
  | { readonly status: 'INVALID'; readonly violations: readonly Violation[] };

/**
 * The cast's capability and limits in `context`, then the context profile's safety on every stage (an orbit's lifetime
 * included). Appraisal and generation's final legality ask this before anything is priced, so they cannot disagree with
 * the loader and the world about whether a plan is within the profile's limits.
 */
export function assessPlanLegality(plan: CompiledSkillPlanV3, context: LegalityContext, grant?: OrbitMeasurementGrant): PlanLegality {
  const capability = assessInContext(plan, context, grant);
  if (capability.status === 'INVALID') return { status: 'INVALID', violations: [{ code: capability.code, severity: 'error', message: capability.message, path: capability.path }] };
  if (capability.status !== 'SUPPORTED') return { status: 'DEFERRED', code: capability.code, message: capability.message };
  const safety = assessCompositeSafety(plan, context.profile);
  return safety.ok ? { status: 'SUPPORTED' } : { status: 'INVALID', violations: safety.violations };
}

function isPersistedContext(context: LegalityContext): boolean {
  const persisted = persistedSkillContext();
  return context.profile === persisted.profile && legalityContextKey(context) === legalityContextKey(persisted);
}

/** Internal pre-numeric projections combine eventual stages; measure their actual refresh behavior without admitting them as authored skills. */
export function loadMeasurementProjection(input: unknown): SkillLoadResult {
  return assessUncached(input, persistedSkillContext(), 0, undefined, true);
}

function assessUncached(input: unknown, context: LegalityContext, castSeed: number, grant: OrbitMeasurementGrant | undefined, measurementProjection = false): SkillLoadResult {
  let canonical: CanonicalSkillV2Input;
  let migrated = false;
  if (isRecord(input) && input.schemaVersion === CANONICAL_SKILL_V2_SCHEMA_VERSION) {
    canonical = input as unknown as CanonicalSkillV2Input;
  } else if (isRecord(input) && input.schemaVersion === CANONICAL_SKILL_V3_SCHEMA_VERSION) {
    // V3 is read here, but not yet executed here. A structurally valid V3 that
    // is behaviourally a plain single attack lowers to V2 and takes the
    // existing compiled path unchanged, which keeps migrated skills
    // byte-stable. Anything genuinely composite is deferred with its authored
    // form preserved rather than reduced to fit the V2 compiler.
    const validated = validateCompositeSkill(input);
    if (!validated.ok) return { status: 'INVALID', issues: validated.issues };
    const lowered = lowerDegenerateV3ToV2(validated.skill as CanonicalSkillV3Input);
    if (lowered === undefined) {
      // Genuinely composite. It reaches READY_COMPOSITE only for a
      // combination the engine has actually verified end to end; the
      // capability owner decides, so the loader cannot open a shape the
      // executor or the evaluator would refuse.
      const compiledComposite = compileCompositeSkill(validated.skill, castSeed, grant);
      if (compiledComposite.status === 'INVALID') return { status: 'INVALID', issues: compiledComposite.issues };
      if (compiledComposite.status === 'DEFERRED') return { status: 'DEFERRED', code: compiledComposite.code, input };
      const capability = assessInContext(compiledComposite.plan, context, grant);
      if (capability.status === 'INVALID') return { status: 'INVALID', issues: [{ path: capability.path, code: capability.code, message: capability.message }] };
      if (capability.status !== 'SUPPORTED') return { status: 'DEFERRED', code: capability.code, input };
      return { status: 'READY_COMPOSITE', plan: compiledComposite.plan, migrated: false };
    }
    const breach = castLimitBreach(validated.skill as CanonicalSkillV3Input, context);
    if (breach !== undefined) return { status: 'INVALID', issues: [breach] };
    canonical = lowered;
    migrated = true;
  } else if (isRecord(input) && typeof input.id === 'string' && 'delivery' in input && Array.isArray(input.effects)) {
    canonical = importLegacySkill(input as unknown as LegacySkillV1);
    migrated = true;
  } else {
    return { status: 'INVALID', issues: [{ path: '', code: 'UNKNOWN_SKILL_FORMAT', message: 'input is neither CanonicalSkillV2 nor LegacySkillV1' }] };
  }
  const compiled = compileCanonicalSkill(canonical);
  if (compiled.status === 'DEFERRED') return { status: 'DEFERRED', code: compiled.code, input: canonical };
  if (compiled.status === 'INVALID') return { status: 'INVALID', issues: compiled.issues };
  const collisions = dotRefreshKeyIssues(canonical.effects, canonical.application);
  if (!measurementProjection && collisions.length > 0) return { status: 'INVALID', issues: collisions };
  return { status: 'READY', skill: compiled.plan.canonicalSkill, migrated };
}
